/**
 * Central location for agreement version constants
 * Update this single location when changing agreement versions
 */
export const AGREEMENT_VERSION = 'v2.0';

/**
 * Legal document versions
 * Track versions for all legal documents
 */
export const LEGAL_VERSIONS = {
  TERMS_OF_SERVICE: 'v2.1',
  PRIVACY_POLICY: 'v2.2',
  EULA: 'v2.0',
  COOKIE_POLICY: 'v1.0',
  USER_AGREEMENT: 'v2.0',
  DISCLAIMERS: 'v1.2'
} as const;

/**
 * Compliance standards tracked by the platform
 */
export const COMPLIANCE_STANDARDS = {
  SOC2_TYPE_II: { name: 'SOC 2 Type II', certified: true, description: 'Service Organization Control — security, availability, processing integrity, confidentiality, and privacy' },
  ISO_27001: { name: 'ISO 27001', certified: true, description: 'Information Security Management System (ISMS)' },
  ISO_42001: { name: 'ISO 42001', certified: true, description: 'AI Management System — responsible AI governance' },
  PCI_DSS: { name: 'PCI-DSS', certified: true, description: 'Payment Card Industry Data Security Standard — via Stripe' },
  GDPR: { name: 'GDPR', certified: true, description: 'EU General Data Protection Regulation' },
  CCPA: { name: 'CCPA/CPRA', certified: true, description: 'California Consumer Privacy Act / California Privacy Rights Act' },
  COPPA: { name: 'COPPA', certified: true, description: "Children's Online Privacy Protection Act — age-gating enforced" },
  HIPAA: { name: 'HIPAA', certified: false, description: 'Health Insurance Portability — not applicable but monitored' },
  SOX: { name: 'SOX', certified: false, description: 'Sarbanes-Oxley Act — future consideration' },
} as const;

/**
 * Version history for transparency
 */
export const VERSION_HISTORY = {
  EULA: [
    { version: 'v2.0', date: '2026-03-09', changes: 'Meta-grade comprehensive update: Added AI processing consent (ISO 42001), GDPR/CCPA data processing rights, PCI-DSS payment security, binding arbitration clause, class action waiver, user conduct policy, device data disclosure, accessibility statement, 30-day modification notice' },
    { version: 'v1.0', date: '2025-10-10', changes: 'Initial EULA release with comprehensive licensing terms' }
  ],
  COOKIE_POLICY: [
    { version: 'v1.0', date: '2025-10-10', changes: 'Initial Cookie Policy release for GDPR/CCPA compliance' }
  ],
  TERMS_OF_SERVICE: [
    { version: 'v2.1', date: '2025-10-10', changes: 'Comprehensive update: Added payment & billing terms, user conduct rules, beta features, third-party services, arbitration clause, and service availability sections' },
    { version: 'v2.0', date: '2025-05-10', changes: 'Enhanced terms with arbitration clause and updated payment terms' },
    { version: 'v1.1', date: '2025-01-15', changes: 'Added calculator disclaimer and beta access terms' }
  ],
  PRIVACY_POLICY: [
    { version: 'v2.2', date: '2025-10-10', changes: 'Major update: Added GDPR rights (EU), CCPA rights (California), third-party service providers, cookies & tracking, children\'s privacy, international data transfers, data breach notification, and U.S. state privacy laws' },
    { version: 'v2.1', date: '2025-05-10', changes: 'Added CCPA compliance section and data retention policy' },
    { version: 'v2.0', date: '2025-03-01', changes: 'GDPR compliance update with expanded data rights' }
  ],
  USER_AGREEMENT: [
    { version: 'v2.0', date: '2026-03-09', changes: 'Replaced NDA-only agreement with comprehensive EULA acceptance gate' },
    { version: 'v1.1', date: '2025-01-15', changes: 'Current version with beta access acknowledgment' }
  ],
  DISCLAIMERS: [
    { version: 'v1.2', date: '2025-10-10', changes: 'Comprehensive update: Added calculator-specific disclaimers (Buying Power, DTI, Mortgage, SmartBuy, Property Tax), beta software disclaimer, AI & automation disclaimer, real-time data disclaimer, professional services disclaimer, and regional variations disclaimer' },
    { version: 'v1.1', date: '2025-05-10', changes: 'Enhanced financial calculator disclaimers' }
  ]
} as const;
